import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useDataDomains } from '../../graphql/queries';
import {
  DataTable,
  StatusBadge,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
  type SortDirection,
} from '../../components/shared';
import type { DataDomain, DataDomainType, DataClassification } from '../../graphql/types';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/information/')({
  component: DataDomainsListPage,
  /* c8 ignore next 4 */
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
  }),
});

export { DataDomainsListPage };

interface DataDomainFilters {
  search: string;
  domainType: DataDomainType | '';
  classification: DataClassification | '';
}

const ForgeButton = 'forge-button' as React.ElementType;

function DataDomainsListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize } = Route.useSearch();

  const [filters, setFilters] = useState<DataDomainFilters>({
    search: '',
    domainType: '',
    classification: '',
  });

  const [sortColumn, setSortColumn] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const { data, loading, error } = useDataDomains();

  const domains = data?.dataDomains?.edges?.map((edge) => edge.node) || [];

  const filteredDomains = domains
    .filter((domain) => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesName = domain.name.toLowerCase().includes(searchLower);
        const matchesDescription = domain.description?.toLowerCase().includes(searchLower);
        if (!matchesName && !matchesDescription) return false;
      }
      if (filters.domainType && domain.domainType !== filters.domainType) return false;
      if (filters.classification && domain.defaultClassification !== filters.classification)
        return false;
      return true;
    })
    .sort((a, b) => {
      if (!sortColumn || !sortDirection) return 0;
      const aValue = a[sortColumn as keyof DataDomain] as string;
      const bValue = b[sortColumn as keyof DataDomain] as string;
      const comparison = aValue?.localeCompare(bValue) || 0;
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  const totalCount = filteredDomains.length;
  const startIndex = (page - 1) * pageSize;
  const pagedDomains = filteredDomains.slice(startIndex, startIndex + pageSize);

  const columns: DataTableColumn<DataDomain>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: '25%',
      render: (domain) => (
        <div>
          <div className="entity-list-name-cell">{domain.name}</div>
          {domain.description && (
            <div className="entity-list-description-cell">
              {domain.description.length > 60
                ? `${domain.description.slice(0, 60)}…`
                : domain.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'domainType',
      label: 'Domain Type',
      sortable: true,
      width: '15%',
      render: (domain) => <span className="text-capitalize">{domain.domainType}</span>,
    },
    {
      key: 'defaultClassification',
      label: 'Classification',
      sortable: true,
      width: '15%',
      render: (domain) => (
        <StatusBadge variant="classification" value={domain.defaultClassification} />
      ),
    },
    {
      key: 'criticalityLevel',
      label: 'Criticality',
      sortable: true,
      width: '12%',
      render: (domain) => <span className="text-capitalize">{domain.criticalityLevel}</span>,
    },
    {
      key: 'lifecycleStatus',
      label: 'Lifecycle',
      sortable: true,
      width: '12%',
      render: (domain) => <span className="text-capitalize">{domain.lifecycleStatus}</span>,
    },
    {
      key: 'dataStewardId',
      label: 'Data Steward',
      width: '21%',
      render: (domain) => domain.dataStewardId || '—',
    },
  ];

  const handleSort = (column: string, direction: SortDirection): void => {
    /* c8 ignore next */
    setSortColumn(column);
    /* c8 ignore next */
    setSortDirection(direction);
  };

  const handleRowClick = (domain: DataDomain): void => {
    void navigate({
      to: '/information/$dataDomainId',
      params: { dataDomainId: domain.entityId },
    });
  };

  const handleFilterChange = (newFilters: Partial<DataDomainFilters>): void => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    void navigate({ to: '/information', search: { page: 1, pageSize } });
  };

  const handleClearFilters = (): void => {
    setFilters({ search: '', domainType: '', classification: '' });
    void navigate({ to: '/information', search: { page: 1, pageSize } });
  };

  const handlePageChange = (newPage: number): void => {
    /* c8 ignore next */
    void navigate({ to: '/information', search: { page: newPage, pageSize } });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    /* c8 ignore next */
    void navigate({ to: '/information', search: { page: 1, pageSize: newPageSize } });
  };

  const hasActiveFilters = filters.search || filters.domainType || filters.classification;

  if (error) {
    return (
      <div className="data-domains-list-page">
        <h1 className="forge-typography--headline4">Data Domains</h1>
        <div className="entity-list-error-state">
          <EmptyState
            icon="error"
            title="Failed to load data domains"
            description={error.message}
            actionLabel="Retry"
            /* c8 ignore next */
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="data-domains-list-page">
      <div className="page-header page-header--toolbar page-header--spaced">
        <div className="page-header-title">
          <h1 className="forge-typography--headline4">Data Domains</h1>
          <span className="forge-typography--body2 page-header-count">
            {loading ? 'Loading...' : `${totalCount} data domains`}
          </span>
        </div>
        <PermissionGate permission="information:create">
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => void navigate({ to: '/information/new' })}
          >
            Create Data Domain
          </ForgeButton>
        </PermissionGate>
      </div>

      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search data domains..."
            aria-label="Search data domains"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </div>
        <div className="filter-selects">
          <select
            className="filter-select"
            aria-label="Filter by domain type"
            value={filters.domainType}
            onChange={(e) =>
              handleFilterChange({ domainType: e.target.value as DataDomainType | '' })
            }
          >
            <option value="">All Domain Types</option>
            <option value="master">Master</option>
            <option value="reference">Reference</option>
            <option value="transactional">Transactional</option>
            <option value="analytical">Analytical</option>
            <option value="metadata">Metadata</option>
          </select>

          <select
            className="filter-select"
            aria-label="Filter by classification"
            value={filters.classification}
            onChange={(e) =>
              handleFilterChange({ classification: e.target.value as DataClassification | '' })
            }
          >
            <option value="">All Classifications</option>
            <option value="public">Public</option>
            <option value="internal">Internal</option>
            <option value="confidential">Confidential</option>
            <option value="restricted">Restricted</option>
          </select>

          {hasActiveFilters && (
            <ForgeButton type="button" variant="outlined" onClick={handleClearFilters}>
              Clear Filters
            </ForgeButton>
          )}
        </div>
      </div>

      <div className="data-domains-table-container">
        {loading ? (
          <TableSkeleton rows={10} columns={6} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={pagedDomains}
              getRowKey={(domain) => domain.entityId}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              emptyState={{
                icon: 'dataset',
                title: 'No data domains found',
                description: hasActiveFilters
                  ? 'Try adjusting your filters'
                  : 'Create your first data domain to get started',
                actionLabel: 'Create Data Domain',
                /* c8 ignore next */
                onAction: () => void navigate({ to: '/information/new' }),
              }}
            />
            <Pagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={page}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>
    </div>
  );
}
